import {
  EPOST_STATUS_TIMELINE_TRACKING_ITEM,
  EPOST_STATUS_TIMELINE_TRACKING_STATUS_TITLES,
  EPOST_STATUS_TIMELINE_TRACKING_SUCCESS_STATUSES_ORDER,
  EpostStatusTimelineTrackingStatus,
} from './EpostStatusTimeline.constants'

export function buildEpostStatusTimeline(raw) {
  const { Error, Print, Sent } = EpostStatusTimelineTrackingStatus

  const acc = EPOST_STATUS_TIMELINE_TRACKING_SUCCESS_STATUSES_ORDER.reduce(
    (acc, code, index, arr) => {
      const { steps } = acc

      // Base step template for each status in the success flow
      const step = {
        ...EPOST_STATUS_TIMELINE_TRACKING_ITEM,
        code,
        titleKey: EPOST_STATUS_TIMELINE_TRACKING_STATUS_TITLES[code],
      }

      const ownDate = raw[code]
      const sentDate = raw[Sent]

      // Normal completion: backend explicitly returns this status with a date
      if (ownDate) {
        step.completed = true
        step.date = ownDate
      }

      /* Special case:
        - backend never sends Print (4),
        - but if Sent (6) exists, we treat Print as completed too.
        NOTE: we reuse Sent date here – if you ever need a separate Print date,
        this logic will need to be revisited. 
      */
      if (code === Print && sentDate && !raw[Print]) {
        step.completed = true
        step.date = sentDate
      }

      if (step.completed) {
        /* Index of the last completed step *in the success flow* (1,2,3,4,6).
          This is used later to:
          1) insert Error right after it,
          2) mark the next step as `current` when there is no error.
        */
        acc.lastCompletedIndex = steps.length
      }

      steps.push(step)

      // On the last reduce iteration we know the full success flow
      if (index === arr.length - 1) {
        if (acc.hasError) {
          // NOTE: if you want to show the error date in the UI,
          // you probably want `date: acc.errorDate` and maybe completed=true.
          const errorStep = {
            ...EPOST_STATUS_TIMELINE_TRACKING_ITEM,
            code: Error,
            isError: true,
            titleKey: EPOST_STATUS_TIMELINE_TRACKING_STATUS_TITLES[Error],
          }

          // Insert Error right after the last completed success step.
          // If nothing is completed, Error becomes the very first item.
          const insertIndex =
            acc.lastCompletedIndex === -1 ? 0 : acc.lastCompletedIndex + 1

          steps.splice(insertIndex, 0, errorStep)

          // Mark the last completed success step before Error.
          // This is used in UI for special styling (connector to error, etc.).
          if (acc.lastCompletedIndex !== -1) {
            steps[acc.lastCompletedIndex].isLastCompletedBeforeError = true
          }

          // NOTE: when there is an error we intentionally do NOT set any `current` step –
          // the whole flow is considered broken at the error point.
        } else {
          // No error in the flow: we need to mark the "in progress" step.

          if (acc.lastCompletedIndex === -1) {
            // Nothing has been completed yet – the very first status is current.
            steps[0].current = true
          } else if (acc.lastCompletedIndex < steps.length - 1) {
            // The next status after the last completed one becomes current.
            steps[acc.lastCompletedIndex + 1].current = true
          }
          // If the last completed step is the final one in the flow,
          // we don't mark any `current` – everything is done.
        }

        // Mark the very last item in the final list
        // (used in UI to hide the bottom connector line).
        steps[steps.length - 1].isLast = true
      }

      return acc
    },
    {
      // Flag that indicates whether an Error status exists in raw payload
      hasError: !!raw[EpostStatusTimelineTrackingStatus.Error],
      lastCompletedIndex: -1,
      steps: [],
    }
  )

  return acc.steps
}
